import axios from 'axios';

const BASE_URL = '/api/v1/media';

/**
 * Media upload response type
 */
export interface MediaUploadResponse {
  videoUrl: string;
  audioUrl: string;
}

/**
 * Generate fallback mock URLs
 */
const generateMockUrls = (fileName: string): MediaUploadResponse => {
  const timestamp = Date.now();
  const fileBase = (fileName && typeof fileName === 'string') 
    ? fileName.split('.')[0] 
    : `file_${timestamp}`;
  
  return {
    videoUrl: `https://mock-s3-bucket.amazonaws.com/videos/${fileBase}_${timestamp}.mp4`,
    audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
  };
};

export const MediaService = {
  /**
   * Upload a media file (audio or video) and save to S3
   * @param file The file to upload
   * @param isVideo Whether the file is a video
   */
  uploadMedia: async (file: File, isVideo: boolean = true): Promise<MediaUploadResponse> => {
    const formData = new FormData();
    formData.append('media', file);
    formData.append('isVideo', isVideo.toString());
    
    try {
      const response = await axios.post(`${BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const data = response.data;
      const timestamp = Date.now();
      const fileBase = file && file.name ? file.name.split('.')[0] : `file_${timestamp}`;
            
      // Handle the video+audio response structure
      if (data.data?.video) {
        const videoUrl = data.data.video.url;
        
        // If audio extraction failed, use mock audio URL
        if (!data.data.audio?.url) {
          console.warn('Audio extraction failed, using mock audio URL');
          return {
            videoUrl,
            audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
          };
        }
        
        return {
          videoUrl,
          audioUrl: data.data.audio.url
        };
      } 
      // Handle audio-only files
      else if (data.data?.audio) {
        const audioUrl = data.data.audio.url;
        return {
          videoUrl: ``,
          audioUrl
        };
      }
      // Handle other response formats
      else if (data.data?.url) {
        // Legacy or simple response format
        if (!isVideo) {
          return {
            videoUrl: ``,
            audioUrl: data.data.url
          };
        } else {
          return {
            videoUrl: data.data.url,
            audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
          };
        }
      }
      // Fallback
      else {
        return generateMockUrls(file?.name);
      }
    } catch (error) {
      console.error('Media upload failed:', error);
      return generateMockUrls(file?.name);
    }
  },

  /**
   * Transcribe an audio file
   */
  transcribeAudio: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('audio', file);
    
    try {
      const response = await axios.post(`${BASE_URL}/audio/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Audio transcription failed:', error);
      throw new Error('Failed to transcribe audio');
    }
  },

  /**
   * Generate TTS audio
   */
  generateTTSAudio: async (text: string, appGuid: string): Promise<any> => {
    const response = await axios.post(`${BASE_URL}/audio/tts`, { text, appGuid });
    return response.data;
  },

  /**
   * Extract audio from a video file
   */
  extractAudio: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('video', file);
    
    try {
      const response = await axios.post(`${BASE_URL}/video/extract-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Audio extraction failed:', error);
      throw new Error('Failed to extract audio from video');
    }
  }
};